import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { saveAuth } from "../auth";
import API_BASE_URL from "../services/config";
import { normalizeAuthUser } from "../utils/normalizeAuthUser";

const OAUTH_EXCHANGE_ENDPOINT = `${API_BASE_URL}/api/auth/oauth/exchange`;

const AuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState("Completing Google sign-in...");
  const [error, setError] = useState("");

  useEffect(() => {
    let isActive = true;
    let redirectTimer = null;

    const completeCallback = async () => {
      const query = new URLSearchParams(location.search);
      const payload = Object.fromEntries(query.entries());

      if (payload.error || payload.message) {
        if (!isActive) return;
        setError(payload.error || payload.message);
        setStatus("");
        return;
      }

      try {
        let authPayload = payload;

        if (payload.code) {
          if (isActive) {
            setStatus("Verifying Google sign-in...");
          }

          const exchangeResponse = await fetch(
            `${OAUTH_EXCHANGE_ENDPOINT}?code=${encodeURIComponent(payload.code)}`,
            {
              method: "GET",
              headers: {
                Accept: "application/json",
              },
            }
          );

          const exchangeData = await exchangeResponse.json().catch(() => ({}));

          if (!exchangeResponse.ok) {
            throw new Error(
              exchangeData.message ||
                exchangeData.error ||
                "Google sign-in could not be completed. Please try again."
            );
          }

          authPayload = exchangeData;
        }

        const normalizedPayload = {
          id:
            authPayload.id ||
            authPayload.userId ||
            authPayload.user_id ||
            authPayload.user?.id ||
            null,
          name:
            authPayload.name ||
            authPayload.fullName ||
            authPayload.full_name ||
            authPayload.user?.name ||
            "",
          email: authPayload.email || authPayload.user?.email || "",
          username:
            authPayload.username ||
            authPayload.userName ||
            authPayload.user_name ||
            authPayload.user?.username ||
            "",
          role: authPayload.role || authPayload.user?.role || "USER",
          token:
            authPayload.token ||
            authPayload.access_token ||
            authPayload.accessToken ||
            authPayload.jwt ||
            authPayload.data?.token ||
            authPayload.user?.token ||
            "",
          user: authPayload.user,
          data: authPayload.data,
        };

        const userData = normalizeAuthUser(normalizedPayload);

        if (!userData) {
          throw new Error(
            "Google sign-in returned an incomplete response. Please verify the backend callback payload."
          );
        }

        if (!isActive) return;

        saveAuth(userData);
        setStatus("Google sign-in successful. Redirecting you home...");

        redirectTimer = window.setTimeout(() => {
          navigate("/", { replace: true });
        }, 1200);
      } catch (callbackError) {
        console.error("Google auth callback failed:", callbackError);
        if (!isActive) return;
        setError(
          callbackError.message ||
            "Google sign-in could not be completed. Please try again."
        );
        setStatus("");
      }
    };

    completeCallback();

    return () => {
      isActive = false;
      if (redirectTimer) {
        window.clearTimeout(redirectTimer);
      }
    };
  }, [location.search, navigate]);

  const styles = {
    page: {
      minHeight: "100vh",
      background: "linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%)",
      color: "#ffffff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "24px",
    },
    card: {
      width: "100%",
      maxWidth: "520px",
      background: "rgba(10, 16, 28, 0.92)",
      border: "1px solid rgba(97, 218, 251, 0.25)",
      borderRadius: "24px",
      boxShadow: "0 24px 60px rgba(97, 218, 251, 0.14)",
      padding: "36px 30px",
      textAlign: "center",
    },
    icon: {
      width: "72px",
      height: "72px",
      margin: "0 auto 18px",
      borderRadius: "20px",
      background: "linear-gradient(135deg, #61dafb 0%, #21a1f1 100%)",
      color: "#07121d",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "2rem",
      fontWeight: 900,
      boxShadow: "0 0 24px rgba(97, 218, 251, 0.3)",
    },
    title: {
      fontSize: "1.8rem",
      fontWeight: 800,
      marginBottom: "12px",
      color: "#61dafb",
    },
    text: {
      color: "#c7d8ee",
      lineHeight: 1.6,
      marginBottom: error ? "18px" : 0,
    },
    error: {
      color: "#ffb8b8",
      background: "rgba(255, 107, 107, 0.12)",
      border: "1px solid rgba(255, 107, 107, 0.22)",
      borderRadius: "12px",
      padding: "14px 16px",
      lineHeight: 1.6,
      marginBottom: "18px",
    },
    button: {
      background: "linear-gradient(135deg, #61dafb 0%, #21a1f1 100%)",
      color: "#07121d",
      border: "none",
      borderRadius: "12px",
      padding: "13px 20px",
      fontWeight: 700,
      cursor: "pointer",
    },
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.icon}>G</div>
        <h1 style={styles.title}>Google Auth Callback</h1>
        {status && <p style={styles.text}>{status}</p>}
        {error && <div style={styles.error}>{error}</div>}
        {error && (
          <button type="button" style={styles.button} onClick={() => navigate("/")}>
            Return Home
          </button>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
